/**
 * `cofactor schedule` — schedule recurring runs.
 *
 * Today the only schedulable target is a test-suite run, exposed as the
 * `suite` sub-namespace (`cofactor schedule suite …`). The group is structured
 * so future targets become sibling sub-namespaces (e.g. `schedule crawl`).
 *
 * A scheduled suite run is an automation rule with `triggerSource:'schedule'`
 * and `actionType:'workflow.run-tests'`; the suite is selected by named-suite
 * UUID, by tag, or "all published".
 *
 * @module schedule
 */

import process from 'node:process';
import { getArgValue, hasFlag, resolveConfig } from './auth.js';
import {
  buildScheduleTriggerConfig,
  collectList,
  createRule,
  fail,
  handleDelete,
  handleGet,
  handleList,
  handlePauseResume,
  handleRun,
  handleRuns,
  hasTimingFlags,
  updateRule,
  type TriggerConfig,
} from './automation-shared.js';

const ACTION_TYPE = 'workflow.run-tests';

function printScheduleHelp(): void {
  console.log(
    [
      'Usage: cofactor schedule <target> <sub-command> [options]',
      '',
      'Targets:',
      '  suite     Schedule a test-suite run',
      '',
      'cofactor schedule suite sub-commands:',
      '  list [--enabled true|false] [--page N] [--json]',
      '  get <id> [--json]',
      '  create  <flags>                      Create a scheduled suite run',
      '  edit <id> <flags>                    Edit a scheduled suite run',
      '  pause <id>                           Pause (disable) a schedule',
      '  resume <id>                          Resume (enable) a schedule',
      '  delete <id>                          Delete a schedule',
      '  run <id>                             Run now',
      '  runs <id> [--status <s>] [--json]    List recent executions',
      '',
      'create/edit flags:',
      '  --name <s>                           Required on create',
      '  --description <s>',
      '  --disabled                           Create paused (create only)',
      '  Target (exactly one):',
      '    --suite-id <uuid>                  A persisted named suite',
      '    --tag <name>                       Workflows with this tag (repeatable/comma)',
      '    --all-published                    All eligible workflows',
      '  --property-id <uuid>                 Restrict to one application (optional)',
      '  --env-id <uuid>                      Force an environment (optional)',
      '  Timing (exactly one):',
      '    --schedule "<text>"                Natural language, e.g. "every day at 2am"',
      '    --cron "<expr>"                    Cron expression',
      '    --at HH:mm                         Daily at a time',
      '    --every <n>(m|h)                   Interval, e.g. 30m or 2h',
      '  --timezone <IANA>                    Default UTC',
      '',
      'Common: --env prod|dev|local, --json',
      '',
      'Examples:',
      '  # Every weekday at 9am, run workflows tagged "smoke"',
      '  cofactor schedule suite create --name "Weekday smoke" \\',
      '    --tag smoke --schedule "every weekday at 9am"',
      '',
      '  # Nightly full run of all published workflows (cron)',
      '  cofactor schedule suite create --name "Nightly" \\',
      '    --all-published --cron "0 3 * * *"',
      '',
      '  # A named suite, daily at 02:00 Eastern',
      '  cofactor schedule suite create --name "Checkout" \\',
      '    --suite-id <uuid> --at 02:00 --timezone America/New_York',
      '',
      '  cofactor schedule suite list',
      '  cofactor schedule suite pause <id>     # and: resume <id>',
      '  cofactor schedule suite run <id>       # run now',
      '  cofactor schedule suite runs <id>      # recent executions',
    ].join('\n')
  );
}

/** Resolve the suite-selection flags into an `actionConfig`, or null if none. */
function buildTarget(argv: string[]): Record<string, unknown> | null {
  const suiteId = getArgValue(argv, '--suite-id');
  const tags = collectList(argv, '--tag');
  const allPublished = hasFlag(argv, '--all-published');

  const chosen = [suiteId !== undefined, tags.length > 0, allPublished].filter(Boolean).length;
  if (chosen === 0) return null;
  if (chosen > 1) fail('Choose exactly one of --suite-id, --tag, --all-published.');

  const actionConfig: Record<string, unknown> = {};
  if (suiteId !== undefined) actionConfig.suite_id = suiteId;
  else if (tags.length > 0) actionConfig.tags = tags;
  // --all-published ⇒ leave suite_id/tags unset (orchestrator runs all eligible).

  const propertyId = getArgValue(argv, '--property-id');
  if (propertyId) actionConfig.property_id = propertyId;
  const envId = getArgValue(argv, '--env-id');
  if (envId) actionConfig.environment_id = envId;

  return actionConfig;
}

async function handleCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const name = getArgValue(argv, '--name');
  if (!name) fail('--name is required.');

  const actionConfig = buildTarget(argv);
  if (actionConfig === null) {
    fail('A target is required: --suite-id <uuid>, --tag <name>, or --all-published.');
  }

  const triggerConfig = await buildScheduleTriggerConfig(argv, apiUrl, token);
  if (!triggerConfig) {
    fail('A schedule is required: --schedule, --cron, --at, or --every.');
  }

  const body: Record<string, unknown> = {
    name,
    enabled: !hasFlag(argv, '--disabled'),
    triggerSource: 'schedule',
    triggerEvent: 'scheduled',
    triggerConfig,
    actionType: ACTION_TYPE,
    actionConfig,
  };
  const description = getArgValue(argv, '--description');
  if (description !== undefined) body.description = description;

  await createRule(apiUrl, token, body);
}

async function handleEdit(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  if (!id) fail('Usage: schedule suite edit <id> <flags>');
  const rest = argv.slice(1);

  const body: Record<string, unknown> = {};
  const name = getArgValue(rest, '--name');
  if (name !== undefined) body.name = name;
  const description = getArgValue(rest, '--description');
  if (description !== undefined) body.description = description;

  const actionConfig = buildTarget(rest);
  if (actionConfig !== null) body.actionConfig = actionConfig;

  if (hasTimingFlags(rest)) {
    const triggerConfig = (await buildScheduleTriggerConfig(rest, apiUrl, token)) as TriggerConfig;
    body.triggerConfig = triggerConfig;
  }

  await updateRule(apiUrl, token, id, body);
}

async function runScheduleSuite(argv: string[]): Promise<void> {
  const [sub, ...rest] = argv;
  if (!sub || sub === 'help' || hasFlag(argv, '--help', '-h')) {
    printScheduleHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);
  const noun = 'schedule suite';

  switch (sub) {
    case 'list':
      await handleList(
        apiUrl,
        token,
        { triggerSource: 'schedule', actionType: ACTION_TYPE },
        rest,
        noun
      );
      break;
    case 'get':
      await handleGet(rest, apiUrl, token, noun);
      break;
    case 'create':
      await handleCreate(rest, apiUrl, token);
      break;
    case 'edit':
      await handleEdit(rest, apiUrl, token);
      break;
    case 'pause':
      await handlePauseResume(rest, apiUrl, token, false, noun);
      break;
    case 'resume':
      await handlePauseResume(rest, apiUrl, token, true, noun);
      break;
    case 'delete':
      await handleDelete(rest, apiUrl, token, noun);
      break;
    case 'run':
      await handleRun(rest, apiUrl, token, noun);
      break;
    case 'runs':
      await handleRuns(rest, apiUrl, token, noun);
      break;
    default:
      console.error(`Unknown sub-command: ${sub}`);
      printScheduleHelp();
      process.exit(1);
  }
}

export async function runSchedule(argv: string[]): Promise<void> {
  const [target, ...rest] = argv;
  if (!target || target === 'help' || hasFlag(argv, '--help', '-h')) {
    printScheduleHelp();
    return;
  }
  if (target === 'suite') {
    await runScheduleSuite(rest);
    return;
  }
  console.error(`Unknown schedule target: ${target} (only "suite" is supported today)`);
  printScheduleHelp();
  process.exit(1);
}
